import type { OfferedResource } from '../../api/types';

/**
 * What is left for a person to do about the offers.
 *
 * A declared offer leaves the section. Kept, the section would fill up with
 * what already works and stop being read — and the one row that needs somebody
 * would be sitting among the ones that do not.
 *
 * The whole list is still worth counting, which is why this returns both:
 * "nothing is offered" and "all of it is declared" are different situations
 * with different answers. The first means no connector is running, or none of
 * them names a backend; the second means there is nothing to do.
 */
export function pendingOffers(offers: readonly OfferedResource[]): OfferedResource[] {
  return offers.filter((offer) => !offer.declared);
}

/**
 * Whether an offered id can be declared **as it is**.
 *
 * The catalogue's ids are narrower than what a connector will offer: lowercase
 * letters, digits and dashes, at most 63 of them, and not a dash at either end.
 * A connector configured with a hostname — dots and all — offers something no
 * catalogue row can hold.
 *
 * That difference is worth a word on the screen, because the failure it leads
 * to is silent. Declaring a corrected id writes a perfectly good row that has
 * nothing to do with the offer: the connector goes on offering what it always
 * did, the offer stays undeclared, and the obvious move — edit it again —
 * changes nothing. What has to change is the connector's own `--serve`.
 */
export function declarableAsIs(id: string): boolean {
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(id) && id.length <= 63;
}
